/**
 * The payment card — one component for every place money is taken.
 *
 * Approved by the client on 19 Oct as the final payment section for "all
 * payment related things". Eight rows, no payment-mode dropdown: UPI and cash
 * are entered side by side and the split is worked out from them.
 *
 *   Total amount                     — the bill before any discount (read-only)
 *   Discounted amount                — entered
 *   Bill amount to be paid           — Total − Discount            (red)
 *   By UPI                           — entered
 *   By cash                          — entered
 *   Total amount received so far     — UPI + Cash                  (green)
 *   Balance                          — Bill − Received, min 0      (red)
 *   Overpaid amount as an advance    — Received − Bill, min 0      (blue)
 *
 * Amounts are entered calculator-style — digits fill in from the right and two
 * decimals are always shown — so there is never a stuck leading zero to delete,
 * which was the specific complaint the client raised about typing into the box.
 */

import { Card, TextInput } from './ui';
import { money } from '../lib/format';

/** What the parent holds and sends. All amounts are paise. */
export interface PaymentState {
  discountPaise: number;
  discountReason: string;
  cashPaise: number;
  upiPaise: number;
}

/** The parent's state plus the figures derived from it, for the payload. */
export interface PaymentDerived extends PaymentState {
  billPaise: number;
  receivedPaise: number;
  balancePaise: number;
  advancePaise: number;
}

/** The single source of truth for the card's arithmetic. */
export function derivePayment(totalPaise: number, s: PaymentState): PaymentDerived {
  const discountPaise = Math.min(Math.max(0, s.discountPaise), totalPaise);
  const billPaise = totalPaise - discountPaise;
  const receivedPaise = Math.max(0, s.cashPaise) + Math.max(0, s.upiPaise);
  const balancePaise = Math.max(0, billPaise - receivedPaise);
  const advancePaise = Math.max(0, receivedPaise - billPaise);
  return { ...s, discountPaise, billPaise, receivedPaise, balancePaise, advancePaise };
}

/** Calculator-style money entry: digits fill in from the right, two decimals. */
function MoneyEntry({
  valuePaise, onChangePaise, ariaLabel,
}: {
  valuePaise: number;
  onChangePaise: (paise: number) => void;
  ariaLabel: string;
}) {
  return (
    <span className="paycard__field">
      <span className="paycard__cur">₹</span>
      <input
        type="text"
        inputMode="numeric"
        placeholder="0.00"
        aria-label={ariaLabel}
        value={valuePaise > 0 ? money(valuePaise, { symbol: false }) : ''}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
          onChangePaise(digits ? parseInt(digits, 10) : 0);
        }}
      />
    </span>
  );
}

function Computed({ label, hint, amountPaise, tone }: {
  label: string; hint?: string; amountPaise: number;
  tone?: 'bill' | 'received' | 'balance' | 'advance';
}) {
  const dim = tone === 'advance' && amountPaise === 0;
  return (
    <div className="paycard__row paycard__row--sum">
      <span className="paycard__lbl">
        {label}{hint ? <small>{hint}</small> : null}
      </span>
      <span className={`paycard__val${tone ? ` paycard__val--${tone}` : ''}${dim ? ' paycard__val--dim' : ''}`}>
        <span className="paycard__cur">₹</span>{money(amountPaise, { symbol: false })}
      </span>
    </div>
  );
}

export function PaymentCard({
  totalPaise, value, onChange, invoiceLabel, showDiscount = true, totalLabel, totalHint,
}: {
  totalPaise: number;
  value: PaymentState;
  onChange: (next: PaymentState) => void;
  invoiceLabel?: string;
  /** Hide the discount row where a door write-off is not offered (e.g. POD). */
  showDiscount?: boolean;
  totalLabel?: string;
  totalHint?: string;
}) {
  const d = derivePayment(totalPaise, value);
  const set = (patch: Partial<PaymentState>) => onChange({ ...value, ...patch });

  return (
    <Card flush>
      <div className="card__header">
        <h3>Payment</h3>
        {invoiceLabel ? <span className="t-xs t-faint">{invoiceLabel}</span> : null}
      </div>

      <Computed
        label={totalLabel ?? 'Total amount'}
        hint={totalHint ?? (showDiscount ? 'Bill before discount' : undefined)}
        amountPaise={totalPaise}
      />

      {showDiscount && (
        <div className="paycard__row">
          <span className="paycard__lbl">Discounted amount</span>
          <MoneyEntry
            valuePaise={value.discountPaise}
            onChangePaise={(p) => set({ discountPaise: p })}
            ariaLabel="Discounted amount"
          />
        </div>
      )}

      {/* The server requires a reason whenever a discount is given — a write-off
          with no name against it is the one adjustment that repeats itself. */}
      {showDiscount && d.discountPaise > 0 && (
        <div className="paycard__row paycard__row--reason">
          <TextInput
            label="Reason for discount"
            required
            placeholder="e.g. round-off, damaged carton"
            value={value.discountReason}
            maxLength={200}
            onChange={(e) => set({ discountReason: e.target.value })}
          />
        </div>
      )}

      {showDiscount && (
        <Computed label="Bill amount to be paid" amountPaise={d.billPaise} tone="bill" />
      )}

      <div className="paycard__row">
        <span className="paycard__lbl">By UPI</span>
        <MoneyEntry
          valuePaise={value.upiPaise}
          onChangePaise={(p) => set({ upiPaise: p })}
          ariaLabel="Paid by UPI"
        />
      </div>

      <div className="paycard__row">
        <span className="paycard__lbl">By cash</span>
        <MoneyEntry
          valuePaise={value.cashPaise}
          onChangePaise={(p) => set({ cashPaise: p })}
          ariaLabel="Paid by cash"
        />
      </div>

      <Computed label="Total amount received so far" hint="UPI + Cash" amountPaise={d.receivedPaise} tone="received" />
      <Computed label="Balance" hint="Still to collect" amountPaise={d.balancePaise} tone="balance" />
      <Computed
        label="Overpaid amount as an advance"
        hint="Credited to customer account"
        amountPaise={d.advancePaise}
        tone="advance"
      />
    </Card>
  );
}
